import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Globe, Trash2, Clock, User, FileText, CalendarDays } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../api';
import { useIsMobile } from '../hooks/useResponsive';
import { Modal } from '../components/ui';
import MonthCalendar from '../components/MonthCalendar';
import { ContentPreview, formatoColor, hhmm, pad2, giornoLabel } from '../components/content';

/**
 * Calendario unico: eventi (riprese, call, meeting, scadenze) + contenuti del
 * Piano Editoriale in uscita. Per Videomaker e SMM è la vista "cosa succede quando".
 */
const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const TIPI = ['generico', 'chiamata', 'meeting', 'scadenza'];
const TIPO = {
  generico: { label: 'Evento',   color: '#64748b' },
  chiamata: { label: 'Chiamata', color: '#10b981' },
  meeting:  { label: 'Meeting',  color: '#8b5cf6' },
  scadenza: { label: 'Scadenza', color: '#f43f5e' },
};

const EMPTY_FORM = { titolo: '', tipo: 'generico', data_inizio: '', data_fine: '', descrizione: '', cliente_id: '', globale: false };

function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5 }}>{label}</span>
      {children}
    </label>
  );
}

export default function Calendario() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success } = useToast();
  const today = new Date();
  const [anno, setAnno] = useState(today.getFullYear());
  const [mese, setMese] = useState(today.getMonth());
  const [eventi, setEventi] = useState([]);
  const [contenuti, setContenuti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [mostra, setMostra] = useState({ contenuti: true, eventi: true });
  const [cliente, setCliente] = useState('');
  const [preview, setPreview] = useState(null);     // contenuto PED in anteprima
  const [evento, setEvento] = useState(null);       // evento in dettaglio
  const [form, setForm] = useState(null);           // null = modale chiusa
  const [saving, setSaving] = useState(false);
  const canGlobal = user?.ruolo === 'admin' || user?.ruolo === 'direttore';

  const load = useCallback(async () => {
    const da = new Date(anno, mese, 1).toISOString();
    const a = new Date(anno, mese + 1, 0, 23, 59, 59).toISOString();
    const [ev, ped, cl] = await Promise.all([
      api.get('/calendario', { params: { da, a } }).catch(() => ({ data: [] })),
      api.get('/ped', { params: { mese: `${anno}-${pad2(mese + 1)}-01` } }).catch(() => ({ data: [] })),
      api.get('/clienti').catch(() => ({ data: [] })),
    ]);
    setEventi(Array.isArray(ev.data) ? ev.data : []);
    setContenuti(Array.isArray(ped.data) ? ped.data.filter(r => r.stato !== 'Scartato') : []);
    setClienti(Array.isArray(cl.data) ? cl.data : []);
  }, [anno, mese]);

  useEffect(() => { load(); }, [load]);

  const prev = () => { if (mese === 0) { setMese(11); setAnno(a => a - 1); } else setMese(m => m - 1); };
  const next = () => { if (mese === 11) { setMese(0); setAnno(a => a + 1); } else setMese(m => m + 1); };
  const goToday = () => { setAnno(today.getFullYear()); setMese(today.getMonth()); };
  const isCurrent = anno === today.getFullYear() && mese === today.getMonth();

  const contenutiF = useMemo(() => contenuti.filter(r => !cliente || r.cliente_id === cliente), [contenuti, cliente]);
  const eventiF = useMemo(() => eventi.filter(e => !cliente || e.cliente_id === cliente), [eventi, cliente]);

  const items = useMemo(() => [
    ...(mostra.contenuti ? contenutiF.filter(r => r.data_pubblicazione).map(r => ({
      id: `p-${r.id}`, kind: 'ped', raw: r,
      date: String(r.data_pubblicazione).slice(0, 10),
      time: hhmm(r.ora_pubblicazione),
      title: r.titolo_post || r.id_post || 'Contenuto',
      sub: [r.formato, r.cliente_nome].filter(Boolean).join(' · '),
      color: formatoColor(r.formato),
    })) : []),
    ...(mostra.eventi ? eventiF.filter(e => e.data_inizio).map(e => {
      const d = new Date(e.data_inizio);
      return {
        id: `e-${e.id}`, kind: 'evento', raw: e,
        date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
        time: e.tutto_il_giorno ? '' : `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
        title: e.titolo,
        sub: [TIPO[e.tipo]?.label, e.cliente_nome, e.globale ? 'Globale' : null].filter(Boolean).join(' · '),
        color: (TIPO[e.tipo] || TIPO.generico).color,
      };
    }) : []),
  ], [contenutiF, eventiF, mostra]);

  const onItemClick = (it) => (it.kind === 'ped' ? setPreview(it.raw) : setEvento(it.raw));

  const nuovoEvento = (ds) => {
    const d = ds || `${anno}-${pad2(mese + 1)}-${pad2(isCurrent ? today.getDate() : 1)}`;
    setForm({ ...EMPTY_FORM, data_inizio: `${d}T09:00`, data_fine: `${d}T10:00`, cliente_id: cliente });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/calendario', { ...form, cliente_id: form.cliente_id || null, data_fine: form.data_fine || null });
      setForm(null);
      success('Evento salvato');
      load();
    } catch { /* toast globale */ } finally { setSaving(false); }
  };

  const eliminaEvento = async (ev) => {
    if (!window.confirm('Eliminare questo evento?')) return;
    try {
      await api.delete(`/calendario/${ev.id}`);
      setEvento(null);
      success('Evento eliminato');
      load();
    } catch { /* toast globale */ }
  };

  const canDelete = (ev) => ev && (String(ev.utente_id) === String(user?.id) || canGlobal);

  const legend = [
    { label: 'Contenuti PED (colore del formato)', color: '#0ea5e9' },
    ...TIPI.map(t => ({ label: TIPO[t].label, color: TIPO[t].color })),
  ];

  const fmtOra = (iso) => (iso ? new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '');

  return (
    <div className="eden-page">
      {/* Intestazione */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0 }}>{MESI[mese]} <span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>{anno}</span></h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {contenutiF.length} {contenutiF.length === 1 ? 'contenuto' : 'contenuti'} in uscita · {eventiF.length} {eventiF.length === 1 ? 'evento' : 'eventi'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="eden-seg" role="group" aria-label="Mese">
            <button onClick={prev} aria-label="Mese precedente"><ChevronLeft size={16} /></button>
            <button onClick={goToday} className={isCurrent ? 'is-on' : ''}>Oggi</button>
            <button onClick={next} aria-label="Mese successivo"><ChevronRight size={16} /></button>
          </div>
          {!isMobile && (
            <button className="eden-btn is-primary" onClick={() => nuovoEvento()}><Plus size={16} />Nuovo evento</button>
          )}
        </div>
      </div>

      {/* Filtri */}
      <div className="eden-toolbar">
        <div className="eden-scroll-row" style={{ flex: '1 1 auto', minWidth: 0 }}>
          <button className={`eden-chip${mostra.contenuti ? ' is-on' : ''}`} aria-pressed={mostra.contenuti}
            onClick={() => setMostra(m => ({ ...m, contenuti: !m.contenuti }))}>
            <FileText size={14} />Contenuti <span className="eden-chip-n">{contenutiF.length}</span>
          </button>
          <button className={`eden-chip${mostra.eventi ? ' is-on' : ''}`} aria-pressed={mostra.eventi}
            onClick={() => setMostra(m => ({ ...m, eventi: !m.eventi }))}>
            <CalendarDays size={14} />Eventi <span className="eden-chip-n">{eventiF.length}</span>
          </button>
        </div>
        <select className="eden-select" value={cliente} onChange={e => setCliente(e.target.value)}
          aria-label="Filtra per cliente" style={{ height: 36, padding: '0 10px', fontSize: 13, maxWidth: isMobile ? '100%' : 240, flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
          <option value="">Tutti i clienti</option>
          {clienti.map(c => <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>)}
        </select>
      </div>

      <MonthCalendar month={new Date(anno, mese, 1)} items={items} onItemClick={onItemClick}
        onAdd={nuovoEvento} addLabel="Nuovo evento" legend={legend} />

      {/* Anteprima contenuto PED */}
      <ContentPreview r={preview} onClose={() => setPreview(null)}
        onOpen={(r) => { setPreview(null); navigate(`/ped/contenuto/${r.id}`); }} />

      {/* Dettaglio evento */}
      <Modal open={!!evento} onClose={() => setEvento(null)} width={440} title={evento ? (TIPO[evento.tipo] || TIPO.generico).label : ''}
        footer={evento && (
          <>
            {canDelete(evento) && (
              <button className="eden-btn" style={{ color: 'var(--red)', marginRight: 'auto' }} onClick={() => eliminaEvento(evento)}>
                <Trash2 size={15} />Elimina
              </button>
            )}
            <button className="eden-btn" onClick={() => setEvento(null)}>Chiudi</button>
          </>
        )}>
        {evento && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 650, color: 'var(--text)', lineHeight: 1.3 }}>{evento.titolo}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              <Clock size={14} />
              <span style={{ textTransform: 'capitalize' }}>{giornoLabel(String(evento.data_inizio).slice(0, 10), { long: true })}</span>
              {!evento.tutto_il_giorno && <span>· {fmtOra(evento.data_inizio)}{evento.data_fine ? ` – ${fmtOra(evento.data_fine)}` : ''}</span>}
            </div>
            {evento.cliente_nome && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}><User size={14} />{evento.cliente_nome}</div>}
            {evento.globale && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--yellow)' }}><Globe size={14} />Evento globale, visibile a tutto il team</div>}
            {evento.descrizione && <div style={{ fontSize: 13.5, color: 'var(--text)', background: 'var(--bg3)', borderRadius: 9, padding: '10px 12px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{evento.descrizione}</div>}
          </div>
        )}
      </Modal>

      {/* Nuovo evento */}
      <Modal open={!!form} onClose={() => setForm(null)} width={480} title="Nuovo evento">
        {form && (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Titolo">
              <input className="eden-input" value={form.titolo} required autoFocus placeholder="Es. Riprese in negozio"
                onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} />
            </Field>
            <div>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5 }}>Tipo</span>
              <div className="eden-scroll-row">
                {TIPI.map(t => (
                  <button type="button" key={t} className={`eden-chip${form.tipo === t ? ' is-on' : ''}`}
                    onClick={() => setForm(f => ({ ...f, tipo: t }))}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: TIPO[t].color }} />{TIPO[t].label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-grid-2" style={{ gap: 12 }}>
              <Field label="Inizio">
                <input type="datetime-local" className="eden-input" value={form.data_inizio} required
                  onChange={e => setForm(f => ({ ...f, data_inizio: e.target.value }))} />
              </Field>
              <Field label="Fine">
                <input type="datetime-local" className="eden-input" value={form.data_fine}
                  onChange={e => setForm(f => ({ ...f, data_fine: e.target.value }))} />
              </Field>
            </div>
            <Field label="Cliente">
              <select className="eden-select" value={form.cliente_id} style={{ width: '100%' }}
                onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value }))}>
                <option value="">Nessun cliente</option>
                {clienti.map(c => <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>)}
              </select>
            </Field>
            <Field label="Note">
              <textarea className="eden-input" rows={2} value={form.descrizione} placeholder="Opzionale" style={{ resize: 'vertical' }}
                onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))} />
            </Field>
            {canGlobal && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
                <input type="checkbox" checked={form.globale} style={{ width: 16, height: 16, accentColor: 'var(--cyan)' }}
                  onChange={e => setForm(f => ({ ...f, globale: e.target.checked }))} />
                Visibile a tutto il team
              </label>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 4 }}>
              <button type="button" className="eden-btn" style={{ marginRight: 'auto' }}
                onClick={() => navigate(`/ped/contenuto/nuovo?data=${form.data_inizio.slice(0, 10)}${form.cliente_id ? `&cliente_id=${form.cliente_id}` : ''}`)}>
                <FileText size={15} />Crea un contenuto invece
              </button>
              <button type="button" className="eden-btn" onClick={() => setForm(null)}>Annulla</button>
              <button type="submit" className="eden-btn is-primary" disabled={saving}>{saving ? 'Salvo…' : 'Salva evento'}</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
